/**
 * TestModeIndicator - Dev-only floating button for testing error states
 * 
 * Only shows in development mode.
 * Provides quick access to simulate offline, 500 errors, etc.
 */

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { FlaskConical, Wifi, WifiOff, AlertTriangle, Clock, Loader2, X } from 'lucide-react';
import { 
  getSimulation, 
  setSimulation, 
  clearSimulation, 
  subscribeToSimulation,
  getAvailableSimulations,
  SimulationMode 
} from '@/lib/testMode';
import { setGlobalError, useErrorStore } from '@/stores/errorStore';

const isDev = import.meta.env.DEV;

export const TestModeIndicator: React.FC = () => {
  const [currentMode, setCurrentMode] = useState<SimulationMode>(getSimulation());
  const clearAllErrors = useErrorStore(state => state.clearAll);
  
  useEffect(() => {
    return subscribeToSimulation(setCurrentMode);
  }, []);
  
  // Production builds must never expose error simulation UI
  if (!isDev) return null;
  
  const simulations = getAvailableSimulations();
  
  const getIcon = (mode: SimulationMode) => {
    switch (mode) {
      case 'offline': return <WifiOff className="h-4 w-4" />;
      case '500': return <AlertTriangle className="h-4 w-4" />;
      case 'timeout': return <Clock className="h-4 w-4" />;
      case 'slow': return <Loader2 className="h-4 w-4" />;
      default: return <FlaskConical className="h-4 w-4" />;
    }
  };
  
  const handleSelect = (mode: SimulationMode) => {
    if (mode === null) {
      clearSimulation();
    } else {
      setSimulation(mode);
    }
  };
  
  const triggerTestError = () => {
    setGlobalError({
      code: 'FW_SAVE_FAILED',
      title: 'Test Error',
      message: 'This is a test error to verify the ErrorBanner is working correctly.',
      severity: 'error',
      traceId: `test-${Date.now()}`,
      retryAction: () => {
        console.log('Test retry action triggered');
        clearAllErrors();
      },
    });
  };
  
  const triggerCriticalError = () => {
    setGlobalError({
      code: 'FW_UNKNOWN',
      title: 'Critical Test Error',
      message: 'This is a critical error that cannot be dismissed.',
      severity: 'critical',
      traceId: `test-critical-${Date.now()}`,
    });
  };
  
  return (
    <div className="fixed bottom-4 left-4 z-50 flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant={currentMode ? "destructive" : "outline"} 
            size="sm"
            className="gap-2 shadow-lg"
          >
            {getIcon(currentMode)}
            <span className="hidden sm:inline">Test Mode</span>
            {currentMode && (
              <Badge variant="secondary" className="ml-1 text-xs">
                {currentMode}
              </Badge>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuLabel>Simulation Mode</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {simulations.map(sim => (
            <DropdownMenuItem
              key={sim.value || 'none'}
              onClick={() => handleSelect(sim.value)}
              className={currentMode === sim.value ? 'bg-accent' : ''}
            >
              <div className="flex items-center gap-2 w-full">
                {getIcon(sim.value)}
                <div className="flex-1">
                  <div className="font-medium">{sim.label}</div>
                  <div className="text-xs text-muted-foreground">{sim.description}</div>
                </div>
                {currentMode === sim.value && (
                  <Badge variant="default" className="text-xs">Active</Badge>
                )}
              </div>
            </DropdownMenuItem>
          ))}
          
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Quick Actions</DropdownMenuLabel>
          
          <DropdownMenuItem onClick={triggerTestError}>
            <AlertTriangle className="h-4 w-4 mr-2 text-amber-500" />
            Trigger Test Error
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={triggerCriticalError}>
            <AlertTriangle className="h-4 w-4 mr-2 text-red-500" />
            Trigger Critical Error
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={clearAllErrors}>
            <X className="h-4 w-4 mr-2" />
            Clear All Errors
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default TestModeIndicator;
